















































































































































































































































































import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import { firebase } from "@/config/firebase";
import { required } from "vuelidate/lib/validators";
import { Contrato } from "@/typings/store/plugins/easyFirestore/contratos";
import { Cliente } from "@/typings/store/plugins/easyFirestore/clientes";
import { Local } from "@/typings/store/plugins/easyFirestore/locales";
import { aFecha, aISOLocal } from "@/helpers/fechas";

/** El término no puede ser anterior al inicio (fechas "YYYY-MM-DD" del date-picker). */
const finDespuesDeInicio = (
  fin: string,
  form: { fechaInicio?: string }
): boolean => !fin || !form.fechaInicio || fin >= form.fechaInicio;

const mayorQueCero = (valor: unknown): boolean => Number(valor) > 0;

@Component({
  validations: {
    form: {
      clienteId: { required },
      localId: { required },
      fechaInicio: { required },
      fechaFin: { required, finDespuesDeInicio },
      superficieTotal: { required, mayorQueCero },
    },
  },
})
export default class FormularioContrato extends Vue {
  @Prop() readonly contrato!: Contrato | null;
  @Prop() readonly cargando!: boolean;
  @Prop({ default: () => [] }) readonly clientes!: Cliente[];
  @Prop({ default: () => [] }) readonly locales!: Local[];
  @Prop({ default: 0 }) readonly codigoContrato!: number;

  // MEJORA: El formulario interno es un reflejo de la BD.
  // Se inicializa vacío.
  form: Partial<Contrato> & { clienteId?: string; localId?: string } = {};

  pdfFile: File | null = null;
  subiendoPdf = false;
  errorPdf = "";
  menuInicio = false;
  menuFin = false;

  // MEJORA: Opciones centralizadas para los campos v-select
  opciones = {
    facturacion: ["Mensual", "Trimestral", "Anual"],
    renovacion: ["Automática", "Manual", "No Renovable"],
    estado: ["Vigente", "Expirado", "Cancelado", "Borrador"],
  };

  created(): void {
    this.resetForm();
    if (this.contrato) {
      // Si estamos editando, llenamos el formulario
      this.form = { ...this.contrato };
      // Las fechas (Timestamp o texto, según cómo se cargó el contrato) pasan a
      // "YYYY-MM-DD" local para el v-date-picker.
      const inicio = aFecha(this.contrato.fechaInicio);
      const fin = aFecha(this.contrato.fechaFin);
      this.form.fechaInicio = (inicio ? aISOLocal(inicio) : "") as never;
      this.form.fechaFin = (fin ? aISOLocal(fin) : "") as never;
    }
    this.id = this.contrato?.id || null;
  }

  id: string | null = null;

  // MEJORA: Watcher para crear el snapshot del cliente automáticamente
  @Watch("form.clienteId")
  onClienteChanged(newId: string | undefined): void {
    if (!newId) return;
    const clienteSeleccionado = this.clientes.find((c) => c.id === newId);
    if (clienteSeleccionado) {
      this.form.clienteSnapshot = {
        codigo: clienteSeleccionado.codigo,
        nombre: clienteSeleccionado.nombre,
        rut: clienteSeleccionado.rut,
        giro: clienteSeleccionado.giro,
        telefono: clienteSeleccionado.telefono,
        direccion: clienteSeleccionado.direccion,
        correo: clienteSeleccionado.correo,
        rubro: clienteSeleccionado.rubro || "", // Asumiendo que `rubro` y `nombre_comercial` existen en tu tipo `Cliente`
        nombre_comercial: clienteSeleccionado.nombre_comercial || "",
      };
    }
  }

  // MEJORA: Watcher para crear el snapshot del local automáticamente
  @Watch("form.localId")
  onLocalChanged(newId: string | undefined): void {
    if (!newId) return;
    const localSeleccionado = this.locales.find((l) => l.id === newId);
    if (localSeleccionado) {
      this.form.localSnapshot = {
        codigo: localSeleccionado.codigo,
        centroCosto: localSeleccionado.centroCosto,
        unidadLocativa: localSeleccionado.unidadLocativa,
        superficie: localSeleccionado.superficie,
        nivel: localSeleccionado.nivel,
        fachada: localSeleccionado.fachada,
        vista_planta: localSeleccionado.vista_planta,
        tipo_local: localSeleccionado.tipo_local,
        estadoLocal: localSeleccionado.estadoLocal,
      };
      // Al crear, la superficie y el centro de costo se copian del local
      if (!this.form.id) {
        this.form.superficieTotal = localSeleccionado.superficie;
        this.form.centroCostoContrato = localSeleccionado.centroCosto;
      }
    }
  }

  get titulo(): string {
    return this.contrato ? "Editar Contrato" : "Ingresar Nuevo Contrato";
  }

  // Formateadores para mostrar las fechas en el text-field
  formatDate(date: string | Date | undefined): string {
    if (!date) return "";
    const [year, month, day] = date.toString().substr(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }

  get fechaInicioFormatted(): string {
    return this.formatDate(this.form.fechaInicio as string | undefined);
  }

  get fechaFinFormatted(): string {
    return this.formatDate(this.form.fechaFin as string | undefined);
  }

  getErrors(fieldName: string): string[] {
    const errors: string[] = [];
    const field = this.$v.form?.[fieldName];
    if (!field || !field.$dirty) return errors;
    if (field.required === false) errors.push("Este campo es obligatorio.");
    else if (field.finDespuesDeInicio === false)
      errors.push("El término no puede ser anterior al inicio.");
    else if (field.mayorQueCero === false)
      errors.push("Debe ser mayor que 0.");
    return errors;
  }

  resetForm(): void {
    this.form = {
      codigo: this.codigoContrato + 1,
      estadoContrato: "Vigente",
      facturacion: "Mensual",
      renovacion: "Manual",
    };
    this.pdfFile = null;
    this.$v.$reset();
  }

  @Emit("close")
  onClose(): void {
    this.resetForm();
  }

  @Emit("click-guardar")
  onClickGuardar(data: Contrato): Contrato {
    return data;
  }

  async guardar(): Promise<void> {
    this.$v.$touch();
    if (this.$v.$invalid) {
      // Puedes usar una notificación toast para avisar al usuario
      // this.$toast.error("Revisa los campos obligatorios.");
      return;
    }

    // Fechas del date-picker ("YYYY-MM-DD") a Timestamp a medianoche local.
    const inicio = aFecha(this.form.fechaInicio) as Date;
    const fin = aFecha(this.form.fechaFin) as Date;
    const doc: Contrato = {
      ...this.form,
      id: this.id, // Si no hay id, se deja como undefined para que Firestore genere uno
      superficieTotal: Number(this.form.superficieTotal),
      fechaInicio: firebase.firestore.Timestamp.fromDate(inicio),
      fechaFin: firebase.firestore.Timestamp.fromDate(fin),
    } as Contrato;

    if (this.pdfFile) {
      try {
        doc.pdfContratoUrl = await this.subirPdf(this.pdfFile, doc.codigo);
      } catch (error) {
        this.errorPdf = "No se pudo subir el PDF. Intenta de nuevo.";
        if (process.env.NODE_ENV !== "production") console.error(error);
        return;
      }
    }

    this.onClickGuardar(doc);
    this.resetForm();
  }

  async subirPdf(archivo: File, codigo: number): Promise<string> {
    this.subiendoPdf = true;
    this.errorPdf = "";
    try {
      const ruta = `contratos/${codigo}/${Date.now()}-${archivo.name}`;
      const snapshot = await firebase.storage().ref(ruta).put(archivo, {
        contentType: "application/pdf",
      });
      return await snapshot.ref.getDownloadURL();
    } finally {
      this.subiendoPdf = false;
    }
  }
}
